import React, { useState, useEffect } from 'react';
import { facilitiesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Euro, CreditCard } from 'lucide-react';

const emptyForm = (fieldId, date) => ({
  field_id: fieldId,
  date: date || formatDateStr(new Date()),
  start_time: '',
  end_time: '',
  notes: '',
  booking_label: '',
  payment_method: 'at_venue',
});

/**
 * Booking dialog shared by the facility page (players) and the management page.
 * Managers also get custom times and a label for the calendar.
 */
export const FacilityBookingDialog = ({ facilityId, fields, isManager, open, onOpenChange, fieldId, onBooked }) => {
  const bookable = fields.filter((f) => f.status !== 'maintenance');
  const [bookingForm, setBookingForm] = useState(() => emptyForm(''));
  const [availability, setAvailability] = useState(null);
  const [customTime, setCustomTime] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    setBookingForm(emptyForm(fieldId || bookable[0]?.field_id || ''));
    setCustomTime(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fieldId]);

  useEffect(() => {
    if (!open || paymentStatus) return;
    facilitiesAPI.paymentStatus(facilityId).then((res) => setPaymentStatus(res.data)).catch(() => {});
  }, [open, facilityId, paymentStatus]);

  useEffect(() => {
    if (!open || !bookingForm.field_id || !bookingForm.date) return;
    let cancelled = false;
    setAvailability(null);
    facilitiesAPI.getFieldAvailability(facilityId, bookingForm.field_id, bookingForm.date)
      .then((res) => !cancelled && setAvailability(res.data))
      .catch(() => !cancelled && setAvailability({ slots: [] }));
    return () => { cancelled = true; };
  }, [facilityId, open, bookingForm.field_id, bookingForm.date]);

  const pickBookingDay = (date) => setBookingForm((prev) => ({ ...prev, date, start_time: '', end_time: '' }));

  const handleSaveBooking = async () => {
    if (!bookingForm.field_id || !bookingForm.date || !bookingForm.start_time || !bookingForm.end_time) {
      toast.error('Scegli un orario');
      return;
    }
    setSavingBooking(true);
    try {
      const res = await facilitiesAPI.createBooking(facilityId, { ...bookingForm });
      const bookingId = res.data?.booking_id;

      // If online payment selected, initiate payment flow
      if (bookingForm.payment_method === 'online' && bookingId) {
        toast.info('Prenotazione creata. Avvio pagamento...');
        onOpenChange(false);
        try {
          const { payBooking } = await import('@/lib/stripe');
          const result = await payBooking(bookingId);
          if (result.error) {
            toast.error(result.error);
          }
          // payBooking redirects to Stripe, so we won't reach here on success
        } catch (payErr) {
          toast.error('Errore nell\'avvio del pagamento. Puoi riprovare dalla sezione prenotazioni.');
        }
      } else {
        toast.success('Prenotazione confermata!');
        onOpenChange(false);
      }
      onBooked?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella prenotazione');
    } finally {
      setSavingBooking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Prenota Campo</DialogTitle>
          <DialogDescription>Scegli campo, giorno e uno degli orari liberi</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 min-w-0">
          {/* Field select */}
          <div className="space-y-2">
            <Label>Campo *</Label>
            <Select
              value={bookingForm.field_id}
              onValueChange={(v) => setBookingForm({ ...bookingForm, field_id: v, start_time: '', end_time: '' })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleziona campo" />
              </SelectTrigger>
              <SelectContent>
                {bookable.map(f => (
                  <SelectItem key={f.field_id} value={f.field_id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Day */}
          <div className="space-y-2">
            <Label>Giorno</Label>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="booking-days">
              {nextDays(21).map((day) => {
                const dateStr = formatDateStr(day);
                const active = bookingForm.date === dateStr;
                return (
                  <button
                    key={dateStr}
                    type="button"
                    onClick={() => pickBookingDay(dateStr)}
                    className={`shrink-0 w-14 py-2 rounded-xl border text-center transition-colors ${
                      active
                        ? 'border-transparent bg-gradient-to-br from-emerald-500 to-blue-600 text-white shadow'
                        : 'border-border hover:bg-muted'
                    }`}
                  >
                    <div className={`text-[11px] uppercase ${active ? 'opacity-90' : 'text-muted-foreground'}`}>
                      {day.toLocaleDateString('it-IT', { weekday: 'short' })}
                    </div>
                    <div className="text-lg font-bold leading-tight">{day.getDate()}</div>
                    <div className={`text-[10px] ${active ? 'opacity-90' : 'text-muted-foreground'}`}>
                      {day.toLocaleDateString('it-IT', { month: 'short' })}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time slots */}
          {!customTime && (
            <div className="space-y-2">
              <Label>Orario</Label>
              {!availability ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-11 rounded-lg" />)}
                </div>
              ) : availability.slots.length === 0 ? (
                <p className="text-sm text-muted-foreground p-3 rounded-lg bg-muted/50 text-center">
                  {availability.message || 'Nessun orario disponibile in questo giorno'}
                </p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slots">
                  {availability.slots.map((slot) => {
                    const past = isPastSlot(bookingForm.date, slot.start);
                    const free = slot.available && !past;
                    const picked = bookingForm.start_time === slot.start && bookingForm.end_time === slot.end;
                    return (
                      <button
                        key={slot.start}
                        type="button"
                        disabled={!free}
                        onClick={() => setBookingForm({ ...bookingForm, start_time: slot.start, end_time: slot.end })}
                        className={`h-11 rounded-lg border text-sm font-medium transition-colors ${
                          picked
                            ? 'border-transparent bg-gradient-to-br from-emerald-500 to-blue-600 text-white shadow'
                            : free
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300'
                              : 'border-border text-muted-foreground/50 line-through cursor-not-allowed'
                        }`}
                      >
                        {slot.start}
                      </button>
                    );
                  })}
                </div>
              )}
              {availability?.slot_price != null && (
                <p className="text-xs text-muted-foreground">
                  {availability.slot_duration_minutes} min · €{availability.slot_price} a slot
                </p>
              )}
            </div>
          )}

          {/* Custom time (manager only: tournaments, odd lengths) */}
          {isManager && (
            customTime ? (
              <div className="space-y-2">
                <Input
                  type="date"
                  value={bookingForm.date}
                  onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Inizio *</Label>
                    <Input
                      type="time"
                      value={bookingForm.start_time}
                      onChange={(e) => setBookingForm({ ...bookingForm, start_time: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fine *</Label>
                    <Input
                      type="time"
                      value={bookingForm.end_time}
                      onChange={(e) => setBookingForm({ ...bookingForm, end_time: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCustomTime(true)}
                className="text-xs text-primary hover:underline"
              >
                Altra data o orario personalizzato
              </button>
            )
          )}

          {/* Booking label (manager only) */}
          {isManager && (
            <div className="space-y-2">
              <Label>Etichetta prenotazione</Label>
              <Input
                placeholder="Es: Torneo Under 18, Squadra Rossi..."
                value={bookingForm.booking_label}
                onChange={(e) => setBookingForm({ ...bookingForm, booking_label: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Nome visibile nel calendario</p>
            </div>
          )}

          {/* Payment method choice */}
          <div className="space-y-2">
            <Label>Metodo di pagamento</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={`p-3 rounded-lg border text-sm text-center transition-colors ${
                  bookingForm.payment_method === 'at_venue'
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border hover:bg-muted'
                }`}
                onClick={() => setBookingForm({ ...bookingForm, payment_method: 'at_venue' })}
              >
                <Euro className="h-4 w-4 mx-auto mb-1" />
                Paga al campo
              </button>
              <button
                type="button"
                className={`p-3 rounded-lg border text-sm text-center transition-colors ${
                  bookingForm.payment_method === 'online'
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border hover:bg-muted'
                } ${!paymentStatus?.online_payments_available ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => {
                  if (paymentStatus?.online_payments_available) {
                    setBookingForm({ ...bookingForm, payment_method: 'online' });
                  }
                }}
                disabled={!paymentStatus?.online_payments_available}
              >
                <CreditCard className="h-4 w-4 mx-auto mb-1" />
                Paga online
              </button>
            </div>
            {!paymentStatus?.online_payments_available && (
              <p className="text-xs text-muted-foreground">Pagamento online non disponibile per questa struttura</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Note (opzionale)</Label>
            <Input
              placeholder="Es: Partita amichevole"
              value={bookingForm.notes}
              onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            onClick={handleSaveBooking}
            disabled={savingBooking || !bookingForm.start_time}
            className="bg-gradient-to-br from-emerald-500 to-blue-600 hover:opacity-90"
            data-testid="save-booking-btn"
          >
            {savingBooking
              ? 'Prenotazione...'
              : bookingForm.start_time
                ? `Prenota ${bookingForm.start_time}–${bookingForm.end_time}${availability?.slot_price != null && !customTime ? ` · €${availability.slot_price}` : ''}`
                : 'Prenota'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export function formatDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function nextDays(count) {
  const today = new Date();
  return Array.from({ length: count }, (_, i) => new Date(today.getFullYear(), today.getMonth(), today.getDate() + i));
}

function isPastSlot(dateStr, start) {
  return new Date(`${dateStr}T${start}`) < new Date();
}

export default FacilityBookingDialog;
